import React from 'react';
import { CheckSquare } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
  updateData: (updates: Partial<OnboardingFormData>) => void;
}

const SPECIALISATION_OPTIONS = [
  { value: 'tax-returns', label: 'Tax Returns & Compliance' },
  { value: 'bookkeeping', label: 'Bookkeeping & Payroll' },
  { value: 'business-advisory', label: 'Business Advisory' },
  { value: 'financial-planning', label: 'Financial Planning' },
  { value: 'audit-assurance', label: 'Audit & Assurance' },
  { value: 'gst-compliance', label: 'GST Compliance' },
  { value: 'company-formations', label: 'Company Formations' },
  { value: 'trust-accounting', label: 'Trust Accounting' },
  { value: 'property-investment', label: 'Property Investment' },
  { value: 'estate-planning', label: 'Estate Planning' },
  { value: 'kiwisaver', label: 'KiwiSaver & Retirement' },
  { value: 'xero-certified', label: 'Xero Certified Partner' },
];

const StepSpecialisations: React.FC<Props> = ({ data, updateData }) => {
  const toggleSpecialisation = (value: string) => {
    const current = data.specialisations;
    if (current.includes(value)) {
      updateData({ specialisations: current.filter(s => s !== value) });
    } else {
      updateData({ specialisations: [...current, value] });
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        What does your business specialize in? This helps us train your assistant 
        to highlight your key services. <span className="text-sm">(Optional)</span>
      </p>

      {/* Checkboxes */}
      <div className="grid gap-3 sm:grid-cols-2">
        {SPECIALISATION_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
          >
            <Checkbox
              checked={data.specialisations.includes(option.value)}
              onCheckedChange={() => toggleSpecialisation(option.value)}
            />
            <span className="text-sm text-foreground">{option.label}</span>
          </label>
        ))}
      </div>

      {/* Other */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Other Specialisations
        </label>
        <Textarea
          value={data.specialisationsOther}
          onChange={(e) => updateData({ specialisationsOther: e.target.value })}
          placeholder="Any other services or specialisations not listed above..."
          rows={3}
        />
      </div>

      {data.specialisations.length > 0 && (
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckSquare className="h-5 w-5 text-brand-teal" />
            <span className="font-medium text-foreground">Selected Specialisations</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.specialisations.map((s) => {
              const option = SPECIALISATION_OPTIONS.find(o => o.value === s);
              return (
                <span
                  key={s}
                  className="px-2 py-1 bg-brand-teal/10 text-brand-teal text-xs rounded-full"
                >
                  {option?.label || s}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepSpecialisations;
